import { Box, Divider, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faVideo } from "@fortawesome/free-solid-svg-icons";
import ScheduleIcon from "@mui/icons-material/Schedule";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import moment from "moment";
import { useAppointments } from "../../../hooks/useAppointments";
import { typography } from "../../../utils/typography";
import { semanticColors } from "../../../utils/colors";
require("./styles.css");

interface Props {
  onClick: () => void;
}

export default function VideoCard({ onClick }: Props) {
  const { t } = useTranslation();

  // Top 5 upcoming (the hook already includes consultations started up to 1h ago)
  const { upcoming: allUpcoming } = useAppointments();
  const upcoming = allUpcoming.slice(0, 5);

  return (
    <article className="card" onClick={onClick}>
      {/* Background image */}
      <img
        className="card__background"
        src="menu0.jpg"
        alt={t("home.videoconsultations")}
        width="1920"
        height="2193"
        style={{ objectPosition: "center center" }}
      />

      {/* Default content — fades out on hover via CSS */}
      <div className="card__content video-default-content">
        <div className="card__icon">
          <FontAwesomeIcon
            icon={faVideo}
            style={{ color: "white", fontSize: 28 }}
          />
        </div>
        <h2 className="card__title">{t("home.videoconsultations")}</h2>
      </div>

      {/* Hover overlay — controlled purely by CSS :hover */}
      <Box
        className="video-overlay"
        sx={{
          background: semanticColors.gradient.surface,
          display: "flex",
          flexDirection: "column"
        }}
      >
        {/* Overlay body */}
        <Box sx={{ flex: 1, px: "20px", py: "14px", overflow: "hidden" }}>
          {upcoming.length === 0 ? (
            <Box
              sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 1.5
              }}
            >
              <CalendarMonthIcon
                sx={{ color: "rgba(255,255,255,0.5)", fontSize: 44 }}
              />
              <Typography
                sx={{
                  ...typography.body.l,
                  color: "rgba(255,255,255,0.8)",
                  textAlign: "center"
                }}
              >
                {t("home.noAppointmentsScheduled")}
              </Typography>
              <Typography
                sx={{
                  ...typography.body.m,
                  color: "rgba(255,255,255,0.55)",
                  textAlign: "center"
                }}
              >
                {t("home.clickToSchedule")}
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              {upcoming.map((appt, i) => (
                <Box key={appt.id}>
                  <Box
                    sx={{
                      py: "9px",
                      display: "flex",
                      alignItems: "flex-start",
                      gap: "10px"
                    }}
                  >
                    <ScheduleIcon
                      sx={{
                        color: "rgba(255,255,255,0.7)",
                        fontSize: 16,
                        mt: "3px",
                        flexShrink: 0
                      }}
                    />
                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        sx={{
                          ...typography.body.lBold,
                          color: "#fff",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap"
                        }}
                      >
                        {appt.patientName}
                      </Typography>
                      <Typography
                        sx={{
                          ...typography.body.m,
                          color: "rgba(255,255,255,0.75)"
                        }}
                      >
                        {moment(appt.start).format("DD/MM [·] HH:mm")}
                      </Typography>
                    </Box>
                  </Box>
                  {i < upcoming.length - 1 && (
                    <Divider sx={{ borderColor: "rgba(255,255,255,0.15)" }} />
                  )}
                </Box>
              ))}
            </Box>
          )}
        </Box>

        {/* Overlay footer */}
        <Box
          sx={{
            px: "20px",
            py: "12px",
            borderTop: "1px solid rgba(255,255,255,0.2)",
            display: "flex",
            justifyContent: "flex-end"
          }}
        >
          <Typography sx={{ ...typography.body.mBold, color: "#fff" }}>
            {t("home.viewCalendar")}
          </Typography>
        </Box>
      </Box>
    </article>
  );
}
